import type { QueryClient, QueryKey } from '@tanstack/react-query';
import { DocumentReference, onSnapshot, Query } from 'firebase/firestore';

/**
 * `queryFn` dla hooków, które dane trzymają w cache React Query przez nasłuch `onSnapshot`
 * (effect w hooku robi `setQueryData` przy każdym snapshocie).
 *
 * Dawny wzorzec `queryFn: () => queryClient.getQueryData(queryKey) ?? fallback` miał wyścig:
 * zanim Firestore odpowiedział, queryFn oddawał fallback (null/[]/defaulty), a ten wynik
 * nadpisywał dane ustawione chwilę później przez nasłuch. Objawy: istniejący agent wyglądał jak
 * brak agenta i otwierał się kreator, lista wracała do „pusto” tuż po utworzeniu rekordu.
 *
 * Tu czekamy na PIERWSZY snapshot tego samego dokumentu/zapytania. Firestore współdzieli nasłuch,
 * więc nie ma drugiego odczytu z sieci.
 *
 * Granice wzorca (świadomie, bez duplikowania parsera z effectu):
 * - Zakładamy, że effect z `onSnapshot` stoi w hooku PRZED `useQuery`. Effecty odpalają się po
 *   renderze, a `useQuery` startuje fetch w swoim effekcie/subskrypcji, więc nasłuch effectu jest
 *   zarejestrowany wcześniej i jego callback dostaje snapshot przed naszym. Gdy tak nie jest
 *   (np. effect za `useQuery` albo inny hook bez nasłuchu), oddajemy fallback jak dawniej.
 * - Pierwszy snapshot może pochodzić z lokalnego cache Firestore (`metadata.fromCache`), nie
 *   z serwera. Nie czekamy na serwer: kolejne snapshoty i tak trafiają do cache React Query przez
 *   `setQueryData` w effekcie, więc dane same się poprawią.
 * - Świadome `null` (np. `!snapshot.exists()` → `setQueryData(key, null)`) zostaje `null`;
 *   fallback podstawiamy tylko wtedy, gdy w cache nie ma jeszcze nic (`undefined`).
 * - `signal` (z QueryFunctionContext) przerywa czekanie: odpinamy nasłuch i oddajemy to, co jest.
 */
export const firstSnapshotFromCache = <T>(
  queryClient: QueryClient,
  queryKey: QueryKey,
  ref: DocumentReference | Query | null | undefined,
  fallback: T,
  signal?: AbortSignal,
): Promise<T> => new Promise<T>((resolve) => {
  const fromCache = (): T => {
    const cached = queryClient.getQueryData<T>(queryKey);
    return cached === undefined ? fallback : cached;
  };
  if (!ref || signal?.aborted) {
    resolve(fromCache());
    return;
  }

  let done = false;
  let unsubscribe: (() => void) | null = null;
  const finish = (): void => {
    if (done) return;
    done = true;
    signal?.removeEventListener('abort', finish);
    // przy synchronicznym callbacku `unsubscribe` jeszcze nie istnieje; odpinamy niżej
    unsubscribe?.();
    // mikrozadanie: setQueryData z nasłuchu effectu zdążyło się wykonać w tym samym evencie
    Promise.resolve().then(() => resolve(fromCache()));
  };

  signal?.addEventListener('abort', finish);
  // Query i DocumentReference mają osobne przeciążenia onSnapshot; zachowanie jest to samo
  unsubscribe = onSnapshot(ref as DocumentReference, () => finish(), () => finish());
  if (done) unsubscribe();
});
